import { useEffect } from "react";
import { Ship, Fuel, Container, Anchor } from "lucide-react";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";
import VesselFrame from "@/components/VesselFrame";
import { CONTACT } from "@/components/ContactCards";

const IMG = {
  hero: "https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGNvbnRhaW5lciUyMHZlc3NlbCUyMG1hcml0aW1lfGVufDB8fHx8MTc5MDQxMzM2Nnww&ixlib=rb-4.1.0&q=85",
  tanker: "https://images.unsplash.com/photo-1598625802173-8ff2d54dde02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxNzV8MHwxfHNlYXJjaHw0fHxvaWwlMjB0YW5rZXIlMjBzaGlwJTIwb2NlYW58ZW58MHx8fHwxNzkwNDEzMzc0fDA&ixlib=rb-4.1.0&q=85",
};

const FOCUS = [
  { icon: Ship, title: "Dry Bulk", copy: "Handysize through Capesize — geared and gearless tonnage, assessed against earnings and scrap floor values." },
  { icon: Fuel, title: "Tankers", copy: "Crude, product and chemical units with full class, coating and trade-history diligence before circulation." },
  { icon: Container, title: "Container & MPP", copy: "Feeder to post-Panamax container vessels and multipurpose tonnage for liner and project operators." },
  { icon: Anchor, title: "Offshore & Specialised", copy: "OSVs, tugs, barges and project cargo units placed with qualified regional and international buyers." },
];

const PROCESS = [
  { step: "01", title: "Valuation & Strategy", copy: "The vessel is benchmarked against comparable sales, charter earnings and demolition floor values. We agree a target price and a circulation strategy — open market or private." },
  { step: "02", title: "Confidential Marketing", copy: "Particulars are prepared and circulated to a controlled list of qualified buyers. Owner identity and terms remain protected throughout." },
  { step: "03", title: "Inspections & Negotiation", copy: "We coordinate inspections, manage questions on class records and certificates, and negotiate price, subjects and delivery terms on your behalf." },
  { step: "04", title: "Memorandum of Agreement", copy: "The MOA is settled on industry-standard forms (NSF / SALEFORM), with deposits, documentation and delivery mechanics clearly fixed." },
  { step: "05", title: "Closing & Delivery", copy: "Funds flow through escrow, titles transfer, and the vessel is delivered at the agreed port or range — with post-closing support as required." },
];

export default function SalePurchase() {
  useEffect(() => {
    document.title = "Sale & Purchase — Dice International LLC";
  }, []);

  return (
    <div data-testid="sale-purchase-page">
      <section className="relative overflow-hidden" data-testid="sp-hero">
        <img src={IMG.hero} alt="Container vessel at sea" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030F26] via-[#030F26]/70 to-[#030F26]/40" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 pb-20 pt-32 sm:px-10 lg:pt-40">
          <Overline testId="sp-overline">Services — 01</Overline>
          <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="sp-heading">
            <MaskedLine delay={0.15}>Sale &amp; Purchase</MaskedLine>
            <MaskedLine delay={0.3}>
              <em className="text-[#C5A059]">of ocean-going tonnage.</em>
            </MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="sp-intro">
              Acquisition and disposal of secondhand vessels for owners, investors and operators — executed
              quietly, priced honestly and documented cleanly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="sp-focus-section">
        <Reveal>
          <Overline testId="sp-focus-overline">Segments Covered</Overline>
          <h2 className="mt-6 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="sp-focus-heading">
            Tonnage we move, segment by segment.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="sp-focus-grid">
          {FOCUS.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col border border-[#1E3A6E]/60 bg-[#091B3E]/70 p-7 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5 hover:border-[#C5A059]/60">
                <f.icon className="h-6 w-6 text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-xl font-semibold text-white">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{f.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="sp-process-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <div className="grid grid-cols-1 gap-14">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline testId="sp-process-overline">The Typical Transaction</Overline>
                <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="sp-process-heading">
                  From valuation to delivery.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-slate-400">
                  Every S&amp;P mandate follows a disciplined sequence. You are briefed at each gate; nothing moves
                  without your instruction.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <VesselFrame src={IMG.tanker} alt="Oil tanker underway" testId="sp-tanker-frame" className="mt-10 h-80" />
              </Reveal>
            </div>
            <div className="lg:col-span-8" data-testid="sp-process-steps">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 0.06}>
                  <div className="flex gap-6 border-t border-[#1E3A6E]/50 py-8 first:border-t-0 first:pt-0 sm:gap-10">
                    <span className="font-mono text-sm text-[#C5A059]">{p.step}</span>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-white sm:text-2xl">{p.title}</h3>
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{p.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="sp-enquiry-section">
        <div className="grid grid-cols-1 gap-14">
          <div className="max-w-2xl">
            <Reveal>
              <Overline testId="sp-enquiry-overline">S&amp;P Enquiries</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="sp-enquiry-heading">
                Buying or selling? Brief the desk.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Share the vessel particulars or your acquisition brief. All enquiries are treated as private and
                answered by a senior broker — typically within one business day.
              </p>
              <div className="mt-8 space-y-3 font-mono text-sm text-slate-300">
                <p>
                  <span className="text-slate-500">EMAIL&nbsp;&nbsp;</span>
                  <a data-testid="sp-email-link" href={`mailto:${CONTACT.email}`} className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.email}</a>
                </p>
                <p>
                  <span className="text-slate-500">DIRECT&nbsp;</span>
                  <a data-testid="sp-phone-link" href={`tel:${CONTACT.phoneRaw}`} className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.phoneDisplay}</a>
                </p>
                <p>
                  <span className="text-slate-500">WHATSAPP&nbsp;</span>
                  <a data-testid="sp-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">{CONTACT.phoneDisplay}</a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
